import { action, makeObservable, observable } from 'mobx';
import * as yup from 'yup';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';
import { TEN_PER_PAGE } from '../utils/const';
import { MomentSchema } from '../utils/yup-schemas';
import { ResponseProvinceSchema } from './business-unit';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';

const EventTranslationSchema = yup.object({
    id: yup.number().required(),
    language: yup.string<'en' | 'fr'>().matches(/en|fr/).required(),
    title: yup.string().required().max(255),
    subtitle: yup.string().nullable().max(255),
    description: yup.string().nullable(),
    image: yup.string().required().max(255),
    ctaText: yup.string().required().max(100),
    mapButtonText: yup.string().nullable().max(100),
});

export const YupBusinessUnit = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
});

export const YupProvince = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    businessUnit: YupBusinessUnit.required(),
});

const EventSchema = yup.object({
    id: yup.number().required(),
    timezone: yup.string().required(),
    startDate: new MomentSchema().required(),
    endDate: new MomentSchema().required(),
    isActive: yup.boolean().required(),
    latitude: yup.number().nullable().min(-90).max(90),
    longitude: yup.number().nullable().min(-180).max(180),
    createdAt: new MomentSchema().required(),
    translations: yup.array(EventTranslationSchema).required(),
    eventProvinces: yup.array(ResponseProvinceSchema).nullable(),
});

const EventSearchParamsSchema = yup.object({
    filteredProvinces: yup.string().optional(),
    page: yup.number().default(0),
    perPage: yup.number().default(TEN_PER_PAGE),
    q: yup.string().optional(),
    isActive: yup.boolean().optional(),
});

const EventTranslationRequestSchema = yup.object({
    language: yup.string<'en' | 'fr'>().matches(/en|fr/).required(),
    title: yup.string().required().max(255),
    subtitle: yup.string().nullable().max(255),
    description: yup.string().nullable(),
    image: yup.string().required().max(255),
    ctaText: yup.string().required().max(100),
    mapButtonText: yup.string().nullable().max(100),
});

const EventRequestSchema = yup.object({
    startDate: new MomentSchema().required(),
    endDate: new MomentSchema().required(),
    isActive: yup.boolean().required(),
    latitude: yup.number().nullable().min(-90).max(90),
    longitude: yup.number().nullable().min(-180).max(180),
    translations: yup.array(EventTranslationRequestSchema).required().min(1),
});

const EventsListSchema = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(EventSchema).default([]),
});

export type Event = yup.InferType<typeof EventSchema>;
export type EventSearchParams = yup.InferType<typeof EventSearchParamsSchema>;
export type EventRequest = yup.InferType<typeof EventRequestSchema>;
export type EventTranslationRequest = yup.InferType<
    typeof EventTranslationRequestSchema
>;
export type EventsList = yup.InferType<typeof EventsListSchema>;

export class EventStore extends WithNetworkConcurrency {
    @observable events: EventsList | null = null;
    @observable isFetchingCount = 0;
    @observable isExportingEvents = false;

    constructor(private rootStore: RootStore) {
        super();
        makeObservable(this);
    }

    @action
    async fetchEvents(params: EventSearchParams) {
        const provinces = this.rootStore.applicationSelectionStore
            .getProvincesFromSelectedApplication;
        if (provinces) {
            params.filteredProvinces = provinces;
        }

        this.isFetchingCount++;
        try {
            const { data, err } = await this.rootStore.makeNetworkCall<
                EventsList
            >(
                {
                    method: HttpMethod.GET,
                    url: '/events',
                    params,
                },
                EventsListSchema
            );

            if (!err && data) {
                this.events = data;
            }
        } catch (error) {
            NotificationManager.showError('Failed to fetch events');
            console.error('Error fetching events:', error);
        } finally {
            this.isFetchingCount--;
        }
    }

    @action
    async exportEvents() {
        this.isExportingEvents = true;
        try {
            const { data, err } = await this.rootStore.makeNetworkCall<Blob>({
                method: HttpMethod.GET,
                url: '/events/export',
                responseType: 'blob',
            });

            if (!err && data) {
                const url = window.URL.createObjectURL(new Blob([data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'events.csv');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                NotificationManager.showSuccess('Events exported successfully');
            }
        } catch (error) {
            NotificationManager.showError('Failed to export events');
            console.error('Error exporting events:', error);
        } finally {
            this.isExportingEvents = false;
        }
    }

    @action
    async createEvent(eventData: EventRequest) {
        try {
            const { data, err } = await this.rootStore.makeNetworkCall<Event>(
                {
                    method: HttpMethod.POST,
                    url: '/events',
                    data: eventData,
                },
                EventSchema
            );

            if (!err && data) {
                NotificationManager.showSuccess('Event created successfully');
                return data;
            }
            throw new Error('Failed to create event');
        } catch (error) {
            NotificationManager.showError('Failed to create event');
            throw error;
        }
    }

    @action
    async updateEvent(id: number, eventData: Partial<EventRequest>) {
        try {
            const { err } = await this.rootStore.makeNetworkCall({
                method: HttpMethod.PUT,
                url: `/events/${id}`,
                data: eventData,
            });

            if (!err) {
                NotificationManager.showSuccess('Event updated successfully');
                return true;
            }
            throw new Error('Failed to update event');
        } catch (error) {
            NotificationManager.showError('Failed to update event');
            throw error;
        }
    }

    @action
    async deleteEvent(id: number) {
        try {
            const { err } = await this.rootStore.makeNetworkCall({
                method: HttpMethod.DELETE,
                url: `/events/${id}`,
            });

            if (!err) {
                NotificationManager.showSuccess('Event deleted successfully');
            }
        } catch (error) {
            NotificationManager.showError('Failed to delete event');
            throw error;
        }
    }

    @action
    async getEvent(id: number) {
        try {
            const { data, err } = await this.rootStore.makeNetworkCall<Event>(
                {
                    method: HttpMethod.GET,
                    url: `/events/${id}`,
                },
                EventSchema
            );

            if (!err && data) {
                return data;
            }
            throw new Error('Failed to fetch event');
        } catch (error) {
            NotificationManager.showError('Failed to fetch event');
            throw error;
        }
    }
}
